import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2,
  Loader2,
  AlertCircle,
  Circle,
} from 'lucide-react';
import type { WbInstantiateEvent } from '@/lib/api/services/websiteBuilder';

interface InstantiateProgressProps {
  events: WbInstantiateEvent[];
  status: 'streaming' | 'done' | 'error';
  errorMessage?: string;
  onRetry?: () => void;
  onPickDifferent?: () => void;
}

interface ChecklistItem {
  key: string;
  label: string;
  state: 'done' | 'active' | 'pending' | 'error';
  hint?: string;
}

function buildChecklist(
  events: WbInstantiateEvent[],
  status: 'streaming' | 'done' | 'error',
): ChecklistItem[] {
  const items: ChecklistItem[] = [];
  let idx = 0;
  for (const ev of events) {
    idx += 1;
    switch (ev.type) {
      case 'fetching_template':
        items.push({ key: `t${idx}`, label: 'Fetching template…', state: 'done' });
        break;
      case 'merging_ai_knowledge':
        items.push({ key: `t${idx}`, label: 'Saving your business info…', state: 'done' });
        break;
      case 'creating_site':
        items.push({
          key: `t${idx}`,
          label: `Creating site: "${ev.site.slug}"…`,
          state: 'done',
        });
        break;
      case 'personalizing_page':
        items.push({
          key: `t${idx}`,
          label: `Personalizing ${ev.page_slug} page…`,
          state: 'done',
        });
        break;
      case 'saved_page': {
        const aiOk = ev.ai_status === 'ok';
        items.push({
          key: `t${idx}-${ev.page_id}`,
          label: `Saved ${ev.page_slug} page`,
          state: 'done',
          hint: aiOk
            ? undefined
            : 'AI personalization unavailable — using your business info directly.',
        });
        break;
      }
      case 'done':
        items.push({ key: `t${idx}`, label: 'Opening editor…', state: 'done' });
        break;
      case 'error':
        items.push({
          key: `t${idx}`,
          label: ev.message || 'Something went wrong',
          state: 'error',
        });
        break;
    }
  }

  if (status === 'streaming' && items.length > 0) {
    const last = items[items.length - 1];
    if (last.state === 'done') last.state = 'active';
  }
  return items;
}

export const InstantiateProgress: React.FC<InstantiateProgressProps> = ({
  events,
  status,
  errorMessage,
  onRetry,
  onPickDifferent,
}) => {
  useEffect(() => {
    if (status !== 'error') return; // streaming + done both ignore ESC
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onPickDifferent?.();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [status, onPickDifferent]);

  const items = buildChecklist(events, status);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-live="polite"
      aria-modal="true"
      data-testid="wb-instantiate-overlay"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl p-6">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-gray-900">
            {status === 'error' ? 'Could not create your site' : 'Personalizing your site…'}
          </h2>
          {status !== 'error' && (
            <p className="text-xs text-gray-500 mt-1">
              This takes a few seconds — we're fetching your template and
              rewriting the copy for your business.
            </p>
          )}
        </div>

        <ul className="space-y-3" data-testid="wb-instantiate-checklist">
          {items.map((item) => (
            <li
              key={item.key}
              className="flex items-start gap-3"
              data-testid={`wb-instantiate-step-${item.state}`}
            >
              <ItemIcon state={item.state} />
              <div className="flex-1 min-w-0">
                <div
                  className={`text-sm ${
                    item.state === 'error'
                      ? 'text-red-700'
                      : item.state === 'pending'
                      ? 'text-gray-400'
                      : item.state === 'active'
                      ? 'text-gray-900 font-medium'
                      : 'text-gray-700'
                  }`}
                >
                  {item.label}
                </div>
                {item.hint && (
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {item.hint}
                  </div>
                )}
              </div>
            </li>
          ))}
          {items.length === 0 && status === 'streaming' && (
            <li
              className="flex items-center gap-3 text-sm text-gray-500"
              data-testid="wb-instantiate-step-active"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              Starting…
            </li>
          )}
        </ul>

        {status === 'error' && (
          <div
            className="mt-5 rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-700"
            data-testid="wb-instantiate-error-block"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="flex-1">
                {errorMessage || 'Something went wrong. Please try again.'}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onPickDifferent}
                data-testid="wb-instantiate-pick-different"
              >
                Pick a different template
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={onRetry}
                data-testid="wb-instantiate-retry"
              >
                Try again
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ItemIcon: React.FC<{ state: ChecklistItem['state'] }> = ({ state }) => {
  if (state === 'done') {
    return <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />;
  }
  if (state === 'active') {
    return <Loader2 className="h-4 w-4 text-purple-600 animate-spin mt-0.5 shrink-0" />;
  }
  if (state === 'error') {
    return <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />;
  }
  return <Circle className="h-4 w-4 text-gray-300 mt-0.5 shrink-0" />;
};
